import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { API_BASE_URL, getJsonAuthHeaders } from "../../../utils/api";
import { fetchTeachers } from "./dataService";
import { buildTeacherAssignmentPayloads } from "./teacherAssignmentUtils";
import "./CampusCourseAssignPage.css";

const TEACHERS_KEY = "als_teachers";
const COURSES_BY_CLASS_KEY = "als_courses_by_class";

function loadFromStorage(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    return parsed ?? fallback;
  } catch (err) {
    console.error(`Failed to parse localStorage key "${key}"`, err);
    return fallback;
  }
}

function saveToStorage(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (err) {
    console.error(`Failed to write localStorage key "${key}"`, err);
  }
}

function getTeacherIdentifier(teacher) {
  if (!teacher) return null;
  return [teacher.id, teacher.teacher_id, teacher.teacherId]
    .filter((value) => value !== undefined && value !== null && value !== "")
    .map((value) => String(value))
    .find(Boolean);
}

function normalizeCourseRow(course, classesList = []) {
  const classId = course.class_id ?? null;
  let className = String(course.class_name || "").trim();
  if (classId && Array.isArray(classesList) && classesList.length > 0) {
    const cls = classesList.find((item) => String(item.id) === String(classId));
    if (cls) {
      const sectionStr = cls.section ? ` — ${cls.section}` : "";
      className = `${String(cls.name || "").trim()}${sectionStr}`;
    }
  }
  return {
    id: course.id ?? course.course_id,
    course_id: course.course_id ?? course.id,
    name: String(course.name || course.course_name || "").trim(),
    class_id: classId,
    class_name: className,
    campus_id: course.campus_id ?? null,
  };
}

function normalizeCourseKey(course) {
  return `${String(course.course_id)}:${String(course.class_id ?? "")}`;
}

function normalizeAssignedCourse(course) {
  return {
    id: course.id,
    course_id: course.course_id ?? course.id,
    course_name: String(course.course_name || course.name || "").trim(),
    class_id: course.class_id ?? null,
    class_name: String(course.class_name || "").trim(),
    campus_id: course.campus_id ?? null,
  };
}

function mergeAssignedCourses(localAssigned = [], backendAssigned = []) {
  const mergedByKey = new Map();
  [...localAssigned, ...backendAssigned].forEach((assignment) => {
    const normalized = normalizeAssignedCourse(assignment);
    const key = `${String(normalized.course_id)}:${String(normalized.class_id ?? "")}`;
    if (!mergedByKey.has(key)) {
      mergedByKey.set(key, normalized);
    }
  });
  return Array.from(mergedByKey.values());
}

function mergeTeacherRecords(savedTeachers = [], incomingTeachers = []) {
  const merged = [];
  const byKey = new Map();

  [...savedTeachers, ...incomingTeachers].forEach((teacher) => {
    const key = getTeacherIdentifier(teacher);
    if (!key) return;

    const existing = byKey.get(key) || {};
    byKey.set(key, {
      ...existing,
      ...teacher,
      assigned_courses: Array.isArray(teacher.assigned_courses)
        ? teacher.assigned_courses
        : Array.isArray(existing.assigned_courses)
          ? existing.assigned_courses
          : [],
    });
  });

  byKey.forEach((teacher) => merged.push(teacher));
  return merged;
}

function findTeacherBySelection(teachersList = [], selection) {
  if (selection === null || selection === undefined || selection === "") {
    return null;
  }

  const normalizedSelection = String(selection).trim();
  if (!normalizedSelection) return null;

  return (
    (Array.isArray(teachersList) ? teachersList : []).find((teacher) => {
      const candidate = getTeacherIdentifier(teacher);
      return (
        candidate != null && String(candidate).trim() === normalizedSelection
      );
    }) || null
  );
}

function CampusCourseAssignPage({ onLogout, showToast, readOnly = false }) {
  const { campusId } = useParams();
  const navigate = useNavigate();

  const [campus, setCampus] = useState(null);
  const [classes, setClasses] = useState([]);
  const [teachers, setTeachers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [backendCourses, setBackendCourses] = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState(null);
  const [selectedCourseIds, setSelectedCourseIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const token = localStorage.getItem("als_token");

  const findCourseById = (courseId) => {
    return (
      courses.find(
        (item) =>
          String(item.course_id) === String(courseId) ||
          String(item.id) === String(courseId),
      ) ||
      backendCourses.find(
        (item) =>
          String(item.course_id) === String(courseId) ||
          String(item.id) === String(courseId),
      ) ||
      null
    );
  };

  const getClassDisplayName = (classId) => {
    if (classId == null || classId === "") return "";
    const cls = classes.find((item) => String(item.id) === String(classId));
    if (!cls) return "";
    const sectionStr = cls.section ? ` — ${cls.section}` : "";
    return `${String(cls.name || "").trim()}${sectionStr}`;
  };

  const enrichAssignedCourse = (assignment) => {
    const normalized = normalizeAssignedCourse(assignment);
    const courseMeta = findCourseById(normalized.course_id);
    return {
      ...normalized,
      course_name:
        normalized.course_name ||
        courseMeta?.name ||
        `Course #${normalized.course_id}`,
      class_name:
        normalized.class_name ||
        courseMeta?.class_name ||
        getClassDisplayName(normalized.class_id) ||
        "—",
    };
  };

  // Campus + classes from backend API
  useEffect(() => {
    const fetchData = async () => {
      try {
        const [campusesRes, classesRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/campuses`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/classes?campus_id=${campusId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if ([campusesRes, classesRes].some((r) => r.status === 401)) {
          onLogout && onLogout();
          return;
        }

        const campuses = campusesRes.ok ? await campusesRes.json() : [];
        const campusDetails = Array.isArray(campuses)
          ? campuses.find((item) => String(item.id) === String(campusId)) ||
            null
          : null;

        setCampus(campusDetails);
        setClasses(classesRes.ok ? await classesRes.json() : []);
      } catch (err) {
        console.error("Failed to load campus/class data", err);
        showToast &&
          showToast(
            "error",
            "Load Failed",
            "Could not load data for this campus.",
          );
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [campusId, token, onLogout, showToast]);

  useEffect(() => {
    const loadBackendCourses = async () => {
      try {
        const [coursesRes, courseCampusRes] = await Promise.all([
          fetch(`${API_BASE_URL}/api/courses`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`${API_BASE_URL}/api/course-campus?campus_id=${campusId}`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if ([coursesRes, courseCampusRes].some((r) => r.status === 401)) {
          onLogout && onLogout();
          return;
        }

        const courseData = coursesRes.ok ? await coursesRes.json() : [];
        const courseCampusData = courseCampusRes.ok
          ? await courseCampusRes.json()
          : [];

        setBackendCourses(Array.isArray(courseData) ? courseData : []);

        const campusCourses = Array.isArray(courseCampusData)
          ? courseCampusData
              .filter((link) => link.class_id != null)
              .map((link) =>
                normalizeCourseRow(
                  {
                    id: link.course_id,
                    course_id: link.course_id,
                    name: link.course_name || link.name,
                    class_id: link.class_id,
                    class_name: link.class_name || "",
                    campus_id: link.campus_id,
                  },
                  classes
                )
              )
          : [];

        setCourses(campusCourses);
      } catch (err) {
        console.warn("Failed to load backend course catalog.", err);
      }
    };

    if (campusId && token) {
      loadBackendCourses();
    }
  }, [campusId, token, onLogout, classes]);

  // Teachers and courses from backend/localStorage
  useEffect(() => {
    if (classes.length === 0 && loading) return;

    const loadTeachers = async () => {
      const savedTeachers = loadFromStorage(TEACHERS_KEY, []);
      let allTeachers = savedTeachers;

      try {
        const backendTeachers = await fetchTeachers();
        if (Array.isArray(backendTeachers)) {
          const assignmentsRes = await fetch(
            `${API_BASE_URL}/api/teacher-courses?campus_id=${campusId}`,
            {
              headers: { Authorization: `Bearer ${token}` },
            },
          );

          if (assignmentsRes.status === 401) {
            onLogout && onLogout();
            return;
          }

          const backendAssignments = assignmentsRes.ok
            ? await assignmentsRes.json()
            : [];

          const assignmentsByTeacher = new Map();
          if (Array.isArray(backendAssignments)) {
            backendAssignments.forEach((assignment) => {
              const teacherKey = String(assignment.teacher_id);
              const existing = assignmentsByTeacher.get(teacherKey) || [];
              existing.push(normalizeAssignedCourse(assignment));
              assignmentsByTeacher.set(teacherKey, existing);
            });
          }

          allTeachers = backendTeachers.map((teacher) => {
            const saved = savedTeachers.find(
              (t) => String(t.id) === String(teacher.id),
            );
            const localAssignments = Array.isArray(saved?.assigned_courses)
              ? saved.assigned_courses
              : [];
            const backendAssignmentsForTeacher =
              assignmentsByTeacher.get(String(teacher.id)) || [];

            return {
              ...teacher,
              assigned_courses: mergeAssignedCourses(
                localAssignments,
                backendAssignmentsForTeacher,
              ),
            };
          });
        }
      } catch (err) {
        if (err?.status === 401) {
          onLogout && onLogout();
          return;
        }
        console.warn(
          "Failed to fetch teachers from API, falling back to local teacher cache.",
          err,
        );
      }

      const campusTeachers = allTeachers.filter(
        (t) => String(t.campus_id) === String(campusId),
      );

      const seen = new Set();
      const dedupedTeachers = campusTeachers.filter((t) => {
        const key = t.cnic
          ? `cnic:${String(t.cnic).trim()}`
          : `nc:${String(t.name).trim()}|${String(t.contact_number).trim()}`;
        if (seen.has(key)) return false;
        seen.add(key);
        return true;
      });
      setTeachers(dedupedTeachers);
    };

    loadTeachers();

    if (courses.length === 0) {
      const coursesByClass = loadFromStorage(COURSES_BY_CLASS_KEY, {});
      const flattened = [];
      classes.forEach((cls) => {
        const classCourses = coursesByClass[String(cls.id)] || [];
        classCourses.forEach((course) => {
          const sectionStr = cls.section ? ` — ${cls.section}` : "";
          flattened.push({
            id: course.id,
            course_id: course.id,
            name: course.name,
            class_id: cls.id,
            class_name: `${String(cls.name || "").trim()}${sectionStr}`,
          });
        });
      });
      setCourses(flattened);
    }
  }, [campusId, classes, loading, onLogout, courses.length, token]);

  const handleTeacherSelect = (teacher) => {
    setSelectedTeacherId(getTeacherIdentifier(teacher));
    setSelectedCourseIds([]);
  };

  const toggleCourseSelection = (courseKey) => {
    setSelectedCourseIds((prev) => {
      const isSelected = prev.includes(courseKey);
      if (isSelected) {
        return prev.filter((key) => key !== courseKey);
      } else {
        return [...prev, courseKey];
      }
    });
  };

  const handleAssign = async (e) => {
    if (e) e.preventDefault();
    if (!selectedTeacherId) {
      showToast && showToast("error", "Missing Info", "Select a teacher.");
      return;
    }
    if (selectedCourseIds.length === 0) {
      showToast &&
        showToast("error", "Missing Info", "Select at least one course.");
      return;
    }

    setSubmitting(true);
    try {
      const savedTeachers = loadFromStorage(TEACHERS_KEY, []);
      const allTeachers = mergeTeacherRecords(savedTeachers, teachers);
      const teacher = findTeacherBySelection(allTeachers, selectedTeacherId);
      const teacherIndex = teacher
        ? allTeachers.findIndex(
            (t) => getTeacherIdentifier(t) === getTeacherIdentifier(teacher),
          )
        : -1;

      if (teacherIndex === -1) {
        showToast &&
          showToast(
            "error",
            "Assignment Failed",
            "Selected teacher could not be found.",
          );
        return;
      }

      const resolvedTeacher = allTeachers[teacherIndex];
      const existingAssigned = Array.isArray(resolvedTeacher.assigned_courses)
        ? resolvedTeacher.assigned_courses
        : [];

      const resolvedCourses = [];
      for (const courseKey of selectedCourseIds) {
        const selectedCourse = courses.find(
          (course) => normalizeCourseKey(course) === courseKey,
        );
        if (!selectedCourse) continue;

        let resolvedCourseId = selectedCourse.course_id ?? selectedCourse.id;
        let courseName = String(selectedCourse.name || "").trim();

        const existingCourse = backendCourses.find(
          (course) =>
            String(course.id) === String(resolvedCourseId) ||
            String(course.name).toLowerCase() === courseName.toLowerCase(),
        );

        if (!existingCourse && courseName) {
          const courseRes = await fetch(`${API_BASE_URL}/api/courses`, {
            method: "POST",
            headers: getJsonAuthHeaders(),
            body: JSON.stringify({
              name: courseName,
              code:
                courseName
                  .toUpperCase()
                  .replace(/[^A-Z0-9]+/g, "")
                  .slice(0, 20) || `COURSE-${Date.now()}`,
              description: `Auto-created for ${campus?.name || `campus ${campusId}`}`,
            }),
          });

          if (courseRes.status === 401) {
            onLogout && onLogout();
            return;
          }

          if (!courseRes.ok) {
            let detail = "Could not create the selected course.";
            try {
              const data = await courseRes.json();
              detail = data.detail || detail;
            } catch {
              // ignore
            }
            throw new Error(detail);
          }

          const createdCourse = await courseRes.json();
          resolvedCourseId = createdCourse.id;
          setBackendCourses((prev) =>
            prev.some(
              (course) => String(course.id) === String(createdCourse.id),
            )
              ? prev
              : [...prev, createdCourse],
          );
        }

        const courseCampusPayload = {
          course_id: resolvedCourseId,
          campus_id: Number(campusId),
          class_id: selectedCourse.class_id ?? null,
        };

        let courseCampusRes = await fetch(`${API_BASE_URL}/api/course-campus`, {
          method: "POST",
          headers: getJsonAuthHeaders(),
          body: JSON.stringify(courseCampusPayload),
        });

        if (courseCampusRes.status === 401) {
          onLogout && onLogout();
          return;
        }

        if (!courseCampusRes.ok && courseCampusRes.status !== 400) {
          const fallbackRes = await fetch(
            `${API_BASE_URL}/api/course-campus-link`,
            {
              method: "POST",
              headers: getJsonAuthHeaders(),
              body: JSON.stringify(courseCampusPayload),
            },
          );

          if (fallbackRes.status === 401) {
            onLogout && onLogout();
            return;
          }

          if (fallbackRes.ok || fallbackRes.status === 400) {
            courseCampusRes = fallbackRes;
          } else {
            let detail = "Could not link the selected course to this campus.";
            try {
              const data = await fallbackRes.json();
              detail = data.detail || detail;
            } catch {
              // ignore
            }
            throw new Error(detail);
          }
        }

        resolvedCourses.push({
          ...selectedCourse,
          id: resolvedCourseId,
          course_id: resolvedCourseId,
          class_id: selectedCourse.class_id ?? null,
          class_name: selectedCourse.class_name || "",
        });
      }

      const { newAssignments, alreadyAssigned } =
        buildTeacherAssignmentPayloads({
          teacherId: Number(resolvedTeacher.id ?? selectedTeacherId),
          campusId: Number(campusId),
          selectedCourseIds: resolvedCourses.map((course) => String(course.id)),
          courses: resolvedCourses,
          existingAssignments: existingAssigned,
        });

      if (newAssignments.length === 0 && alreadyAssigned.length > 0) {
        showToast &&
          showToast(
            "error",
            "Already Assigned",
            "All selected courses are already assigned to this teacher.",
          );
        return;
      }

      const addedAssignments = [];
      for (const assignment of newAssignments) {
        const assignmentRes = await fetch(
          `${API_BASE_URL}/api/teacher-courses`,
          {
            method: "POST",
            headers: getJsonAuthHeaders(),
            body: JSON.stringify(assignment),
          },
        );

        if (assignmentRes.status === 401) {
          onLogout && onLogout();
          return;
        }

        if (!assignmentRes.ok) {
          let detail = "Could not assign courses.";
          try {
            const data = await assignmentRes.json();
            detail = data.detail || detail;
          } catch {
            // ignore
          }
          throw new Error(detail);
        }

        const createdAssignment = await assignmentRes.json();
        addedAssignments.push(createdAssignment);
      }

      const updatedAssigned = [
        ...existingAssigned,
        ...addedAssignments.map((assignment) => {
          const course = courses.find(
            (item) => String(item.id ?? item.course_id) === String(assignment.course_id),
          );
          return normalizeAssignedCourse({
            id: assignment.id,
            course_id: assignment.course_id,
            course_name: course?.name || course?.course_name || "",
            class_id: assignment.class_id,
            class_name: course?.class_name || "",
            campus_id: assignment.campus_id,
          });
        }),
      ];

      const updatedTeacher = {
        ...resolvedTeacher,
        assigned_courses: updatedAssigned,
      };

      const updatedTeachers = [...allTeachers];
      updatedTeachers[teacherIndex] = updatedTeacher;
      saveToStorage(TEACHERS_KEY, updatedTeachers);

      let successMessage = `${newAssignments.length} course${newAssignments.length !== 1 ? "s" : ""} assigned successfully.`;
      if (alreadyAssigned.length > 0) {
        successMessage += ` ${alreadyAssigned.length} course${alreadyAssigned.length !== 1 ? "s" : ""} were already assigned.`;
      }

      showToast && showToast("success", "Courses Assigned", successMessage);

      const refreshedTeachers = loadFromStorage(TEACHERS_KEY, []);
      const refreshedCampusTeachers = refreshedTeachers.filter(
        (t) => String(t.campus_id) === String(campusId),
      );
      setTeachers(refreshedCampusTeachers);

      setSelectedTeacherId(null);
      setSelectedCourseIds([]);
    } catch (err) {
      console.error(err);
      showToast &&
        showToast(
          "error",
          "Assignment Failed",
          err.message || "Could not assign courses.",
        );
    } finally {
      setSubmitting(false);
    }
  };

  const handleUnassign = async (assignment) => {
    if (!selectedTeacherId) {
      showToast &&
        showToast("error", "Error", "Please select a teacher first.");
      return;
    }
    const courseId = assignment.course_id;
    const classId = assignment.class_id;

    const courseName = assignment.course_name || "this course";
    const classSuffix = assignment.class_name ? ` for ${assignment.class_name}` : "";

    if (
      !window.confirm(
        `Are you sure you want to unassign "${courseName}"${classSuffix} from this teacher?`,
      )
    ) {
      return;
    }

    setSubmitting(true);
    try {
      const assignedCourses = getTeacherAssignedCourses(selectedTeacherId);
      const matched = assignedCourses.find(
        (c) =>
          String(c.course_id) === String(courseId) &&
          (c.class_id == null || classId == null || String(c.class_id) === String(classId))
      );

      if (matched && matched.id) {
        const deleteRes = await fetch(
          `${API_BASE_URL}/api/teacher-courses/${matched.id}`,
          {
            method: "DELETE",
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (deleteRes.status === 401) {
          onLogout && onLogout();
          return;
        }
        if (!deleteRes.ok) {
          throw new Error("Failed to delete assignment from database.");
        }
      }

      const savedTeachers = loadFromStorage(TEACHERS_KEY, []);
      const allTeachers = mergeTeacherRecords(savedTeachers, teachers);
      const teacher = findTeacherBySelection(allTeachers, selectedTeacherId);
      const teacherIndex = teacher
        ? allTeachers.findIndex(
            (t) => getTeacherIdentifier(t) === getTeacherIdentifier(teacher),
          )
        : -1;

      if (teacherIndex === -1) {
        showToast &&
          showToast(
            "error",
            "Unassign Failed",
            "Selected teacher could not be found.",
          );
        return;
      }

      const resolvedTeacher = allTeachers[teacherIndex];
      const existingAssigned = Array.isArray(resolvedTeacher.assigned_courses)
        ? resolvedTeacher.assigned_courses
        : [];

      const updatedAssigned = existingAssigned.filter(
        (c) =>
          !(
            String(c.course_id) === String(courseId) &&
            (c.class_id == null || classId == null || String(c.class_id) === String(classId))
          )
      );

      const updatedTeacher = {
        ...resolvedTeacher,
        assigned_courses: updatedAssigned,
      };

      const updatedTeachers = [...allTeachers];
      updatedTeachers[teacherIndex] = updatedTeacher;
      saveToStorage(TEACHERS_KEY, updatedTeachers);

      const refreshedTeachers = loadFromStorage(TEACHERS_KEY, []);
      const refreshedCampusTeachers = refreshedTeachers.filter(
        (t) => String(t.campus_id) === String(campusId),
      );
      setTeachers(refreshedCampusTeachers);

      setSelectedCourseIds((prev) =>
        prev.filter((key) => {
          const [cId, clsId] = key.split(":");
          return !(String(cId) === String(courseId) && String(clsId || "") === String(classId || ""));
        })
      );

      showToast &&
        showToast(
          "success",
          "Course Unassigned",
          `"${courseName}" has been unassigned successfully.`,
        );
    } catch (err) {
      console.error(err);
      showToast &&
        showToast("error", "Unassign Failed", err.message || "Could not unassign course.");
    } finally {
      setSubmitting(false);
    }
  };

  const getTeacherAssignedCourses = (teacherId) => {
    const teacher = findTeacherBySelection(teachers, teacherId);
    if (!teacher) return [];
    return Array.isArray(teacher.assigned_courses)
      ? teacher.assigned_courses.map(enrichAssignedCourse)
      : [];
  };

  const getTeacherSubjects = (teacher) => {
    if (!teacher) return [];
    const subjects = Array.isArray(teacher.subjects)
      ? [...teacher.subjects]
      : [];
    if (teacher.other_subject) {
      subjects.push(String(teacher.other_subject).trim());
    }
    return subjects.filter(Boolean);
  };

  const selectedTeacher = selectedTeacherId
    ? findTeacherBySelection(teachers, selectedTeacherId)
    : null;

  const isCourseSelected = (courseKey) => selectedCourseIds.includes(courseKey);

  const isCourseAssigned = (teacherId, course) => {
    if (!course) return false;
    const assignedCourses = getTeacherAssignedCourses(teacherId);
    return assignedCourses.some(
      (c) =>
        String(c.course_id) === String(course.course_id ?? course.id) &&
        (c.class_id == null || course.class_id == null || String(c.class_id) === String(course.class_id))
    );
  };

  const findTeacherAssignedToCourse = (course) => {
    if (!course) return null;
    const courseId = course.course_id ?? course.id;
    const classId = course.class_id;

    for (const t of teachers) {
      if (Array.isArray(t.assigned_courses)) {
        const hasAssignment = t.assigned_courses.some(
          (c) =>
            String(c.course_id) === String(courseId) &&
            (c.class_id == null || classId == null || String(c.class_id) === String(classId))
        );
        if (hasAssignment) {
          return t;
        }
      }
    }
    return null;
  };

  if (loading) {
    return <div className="page-loading">Loading...</div>;
  }

  return (
    <div className="course-assign-page">
      <div className="course-assign-header">
        <button
          className="course-assign-back"
          onClick={() => navigate(`/teachers/courses/campus/${campusId}`)}
        >
          ← Back
        </button>
        <h2>
          {readOnly ? "Teacher Course & Class Assignments" : "Assign Courses"}
          {campus ? ` — ${campus.name}` : ""}
        </h2>
      </div>

      {/* Teachers Table */}
      <div className="teachers-section">
        {teachers.length === 0 ? (
          <div className="empty-state">
            <div className="empty-icon">👨‍🏫</div>
            <div className="empty-text">
              No teachers registered for this campus yet.
              <br />
              Register a teacher first before assigning courses.
            </div>
          </div>
        ) : (
          <div className="table-wrapper">
            <table className="teachers-table">
              <thead>
                <tr>
                  <th style={{ width: "40px" }}>#</th>
                  <th style={{ textAlign: "left" }}>Teacher Name</th>
                  <th style={{ textAlign: "left" }}>CNIC</th>
                  <th style={{ textAlign: "left" }}>Contact</th>
                  <th style={{ textAlign: "left" }}>Subjects</th>
                  <th style={{ textAlign: "left" }}>Status</th>
                  <th style={{ textAlign: "center" }}>Assigned Courses</th>
                  <th style={{ width: "120px" }}>{readOnly ? "View" : "Select"}</th>
                </tr>
              </thead>
              <tbody>
                {teachers.map((teacher, idx) => {
                  const assignedCourses = getTeacherAssignedCourses(teacher.id);
                  const isSelected =
                    String(getTeacherIdentifier(teacher)) ===
                    String(selectedTeacherId);
                  return (
                    <tr
                      key={teacher.id}
                      className={isSelected ? "selected-row" : ""}
                      onClick={() => handleTeacherSelect(teacher)}
                    >
                      <td>{idx + 1}</td>
                      <td style={{ textAlign: "left", fontWeight: 600 }}>
                        {teacher.name}
                      </td>
                      <td style={{ textAlign: "left" }}>
                        {teacher.cnic || "—"}
                      </td>
                      <td style={{ textAlign: "left" }}>
                        {teacher.contact_number || teacher.email || "—"}
                      </td>
                      <td style={{ textAlign: "left" }}>
                        {getTeacherSubjects(teacher).length > 0
                          ? getTeacherSubjects(teacher).slice(0, 3).join(", ") +
                            (getTeacherSubjects(teacher).length > 3
                              ? "..."
                              : "")
                          : "—"}
                      </td>
                      <td style={{ textAlign: "left" }}>
                        {teacher.employment_status || "—"}
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <span className="assigned-count-badge">
                          {assignedCourses.length}
                        </span>
                      </td>
                      <td style={{ textAlign: "center" }}>
                        <button
                          className={`select-teacher-btn ${isSelected ? "selected" : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleTeacherSelect(teacher);
                          }}
                        >
                          {isSelected ? "✓ Viewing" : "View"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Selected Teacher's Courses */}
      {selectedTeacherId && (
        <div className="selected-teacher-section">
          <div className="selected-teacher-header">
            <div>
              <h3>
                {readOnly ? "Assignments for: " : "Assign Courses to: "}
                {selectedTeacher?.name || "Teacher"}
              </h3>
              <div className="selected-teacher-subtitle">
                {selectedTeacher?.campus_id
                  ? `Campus ID: ${selectedTeacher.campus_id}`
                  : "No campus assigned"}
              </div>
            </div>
            <button
              className="deselect-teacher-btn"
              onClick={() => {
                setSelectedTeacherId(null);
                setSelectedCourseIds([]);
              }}
            >
              ✕ Deselect
            </button>
          </div>

          {selectedTeacher && (
            <div className="selected-teacher-meta">
              <div className="meta-row">
                <div className="meta-label">Contact</div>
                <div className="meta-value">
                  {selectedTeacher.contact_number ||
                    selectedTeacher.email ||
                    "—"}
                </div>
              </div>
              <div className="meta-row">
                <div className="meta-label">Email</div>
                <div className="meta-value">{selectedTeacher.email || "—"}</div>
              </div>
              <div className="meta-row">
                <div className="meta-label">CNIC</div>
                <div className="meta-value">{selectedTeacher.cnic || "—"}</div>
              </div>
              <div className="meta-row">
                <div className="meta-label">Status</div>
                <div className="meta-value">
                  {selectedTeacher.employment_status || "—"}
                </div>
              </div>
              <div className="meta-row">
                <div className="meta-label">Subjects</div>
                <div className="meta-value">
                  {getTeacherSubjects(selectedTeacher).join(", ") || "—"}
                </div>
              </div>
              <div className="meta-row">
                <div className="meta-label">Emergency</div>
                <div className="meta-value">
                  {selectedTeacher.emergency_contact_name
                    ? `${selectedTeacher.emergency_contact_name} (${
                        selectedTeacher.emergency_contact_number || "no phone"
                      })`
                    : "—"}
                </div>
              </div>
            </div>
          )}

          {/* Currently Assigned Courses */}
          <div className="assigned-courses-section">
            <div className="subsection-title">Currently Assigned Courses</div>
            {getTeacherAssignedCourses(selectedTeacherId).length === 0 ? (
              <div className="no-assigned-message">
                This teacher has no courses assigned yet.
              </div>
            ) : (
              <div className="assigned-courses-list">
                {getTeacherAssignedCourses(selectedTeacherId).map(
                  (course, idx) => (
                    <div key={idx} className="assigned-course-item">
                      <span className="course-name">
                        {course.course_name || course.name || "Unknown Course"}
                      </span>
                      <span className="course-class">
                        {course.class_name
                          ? `Class: ${course.class_name}`
                          : "Class: —"}
                      </span>
                      {!readOnly && (
                        <button
                          className="unassign-btn"
                          onClick={() => handleUnassign(course)}
                          disabled={submitting}
                        >
                          Unassign
                        </button>
                      )}
                    </div>
                  ),
                )}
              </div>
            )}
          </div>

          {!readOnly && (
            <>
          {/* Available Courses to Assign */}
          <div className="available-courses-section">
            <div className="subsection-title">
              Available Courses (Click row or checkbox to select)
            </div>

            {courses.length === 0 ? (
              <div className="empty-state small">
                <div className="empty-text">
                  No courses available. Add courses first from the "Add Course"
                  page.
                </div>
              </div>
            ) : (
              <div className="table-wrapper">
                <table className="courses-table">
                  <thead>
                    <tr>
                      <th style={{ width: "40px" }}>Select</th>
                      <th style={{ textAlign: "left" }}>Course Name</th>
                      <th style={{ textAlign: "left" }}>Class</th>
                      <th style={{ width: "100px" }}>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {courses.map((course, idx) => {
                      const courseKey = normalizeCourseKey(course);
                      const isSelected = isCourseSelected(courseKey);
                      const isAssigned = isCourseAssigned(
                        selectedTeacherId,
                        course,
                      );
                      const otherTeacher = findTeacherAssignedToCourse(course);
                      const isAssignedToOther = otherTeacher && String(getTeacherIdentifier(otherTeacher)) !== String(selectedTeacherId);
                      
                      const hasClassLink = Boolean(
                        course.class_id || course.class_name,
                      );
                      const isDisabled = isAssigned || isAssignedToOther || !hasClassLink;

                      return (
                        <tr
                          key={courseKey}
                          className={`course-row ${isSelected ? "selected-row" : ""} ${isAssigned ? "assigned-row" : ""} ${isAssignedToOther ? "assigned-other-row" : ""} ${!hasClassLink ? "unlinked-course-row" : ""}`}
                          onClick={() =>
                            !isDisabled && toggleCourseSelection(courseKey)
                          }
                        >
                          <td style={{ textAlign: "center" }}>
                            <input
                              type="checkbox"
                              checked={isSelected || isAssigned || isAssignedToOther}
                              onChange={() =>
                                !isDisabled && toggleCourseSelection(courseKey)
                              }
                              disabled={isDisabled}
                              className="course-checkbox"
                            />
                          </td>
                          <td style={{ textAlign: "left", fontWeight: 500 }}>
                            {course.name ||
                              course.course_name ||
                              "Untitled course"}
                          </td>
                          <td style={{ textAlign: "left" }}>
                            {course.class_name || "—"}
                          </td>
                          <td style={{ textAlign: "center" }}>
                            {isAssigned ? (
                              <span className="assigned-badge">Assigned</span>
                            ) : isAssignedToOther ? (
                              <span className="assigned-other-badge" title={`Assigned to ${otherTeacher.name}`}>
                                Assigned to {otherTeacher.name}
                              </span>
                            ) : !hasClassLink ? (
                              <span className="unlinked-badge">
                                No class link
                              </span>
                            ) : (
                              <span className="available-badge">Available</span>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Action Button */}
          <div className="assign-action-section">
            <button
              className="assign-btn"
              onClick={handleAssign}
              disabled={
                submitting ||
                selectedCourseIds.length === 0 ||
                courses.length === 0
              }
            >
              {submitting
                ? "Assigning..."
                : `Assign ${selectedCourseIds.length} Selected Course${selectedCourseIds.length !== 1 ? "s" : ""}`}
            </button>
            {selectedCourseIds.length > 0 && (
              <span className="selection-hint">
                {selectedCourseIds.length} course
                {selectedCourseIds.length !== 1 ? "s" : ""} selected
              </span>
            )}
          </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

export default CampusCourseAssignPage;
